/**
 * @file App.js
 * @description Root component. Sets up routing, auth, theming,
 * offline indicator, and service worker registration.
 */
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./hooks/useTheme";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DesignThemeProvider } from "./design-system";
import { ToastProvider } from "./design-system";
import "./design-system/tokens.css";
import ThemedApp from "./components/ThemedApp";
import Navbar from "./components/Navbar";
import OfflineIndicator from "./components/OfflineIndicator";
// Mounted once, renders nothing until a "Sign in again" door is opened.
import { ReSignInHost } from "./components/ReSignIn";
import UnderConstructionBanner from "./components/UnderConstructionBanner";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import PricingPage from "./pages/PricingPage";
import FeaturesPage from "./pages/FeaturesPage";
import TermsPage from "./pages/TermsPage";
import LauncherPage from "./pages/LauncherPage";
import Dashboard from "./pages/Dashboard";
import InspectionsPage from "./pages/InspectionsPage";
import NewInspectionPage from "./pages/NewInspectionPage";
import ActiveInspectionPage from "./pages/ActiveInspectionPage";
import InspectionReportPage from "./pages/InspectionReportPage";
import InspectionChangeLogPage from "./pages/InspectionChangeLogPage";
import TemplatesPage from "./pages/TemplatesPage";
import TemplateDetailPage from "./pages/TemplateDetailPage";
import TemplateBuilderPage from "./pages/TemplateBuilderPage";
import SchedulePage from "./pages/SchedulePage";
// The classic CustomersPage / CustomerDetailPage are mounted through
// pages/customer/CustomerPages.js (the look switch), not here (2026-10-14).
import AgentsPage from "./pages/AgentsPage";
import PipelinePage from "./pages/PipelinePage";
import RecurringPage from "./pages/RecurringPage";
import CrmSettingsPage from "./pages/CrmSettingsPage";
// WHY (2026-10-14, James): Customer Butler = Zoho's arrangement in the Butler
// look. These doors pick the clean look or the untouched classic one, and add
// the property list and property record pages.
// WHY (2026-10-14, Honest UI): CRM Setup also stands behind the Customer Butler gate (Manager only).
import { CustomersEntry, CustomerEntry, PropertiesEntry, PropertyEntry, CrmSetupEntry } from "./pages/customer/CustomerPages";
// WHY (2026-10-15, James: "what you made vs the design spec" - HR Butler is
// the spec): Customer Butler pages in the HR look carry ONE header row of
// their own, like HR Butler, so the suite bar and the alpha banner are left
// off on those addresses only. Every other page is unchanged.
import { useHideSuiteShell } from "./pages/customer/customerLook";
// WHY (2026-10-07, James): Zoho bridge phase 1 admin panel; owner/admin only (the server decides).
import BridgePage from "./pages/bridge/BridgePage";
import TasksPage from "./pages/TasksPage";
import SettingsPage from "./pages/SettingsPage";
import BuildLog from "./pages/BuildLog";
import LogoutPage from "./pages/LogoutPage";
import AdminBlockedPatternsPage from "./pages/AdminBlockedPatternsPage";
import ClientPortalPage from "./pages/ClientPortalPage";
import WellHomePage from "./pages/well/WellHomePage";
import SizePumpPage from "./pages/well/SizePumpPage";
import MegTestPage from "./pages/well/MegTestPage";
import DiagnosePage from "./pages/well/DiagnosePage";
import PhotoAuditPage from "./pages/well/PhotoAuditPage";
import TrenchEstimatePage from "./pages/well/TrenchEstimatePage";
import WellHistoryPage from "./pages/well/WellHistoryPage";
import SummarizeReportPage from "./pages/well/SummarizeReportPage";
import NotFoundPage from "./pages/NotFoundPage";
import "./styles/App.css";


/** Redirects unauthenticated users to /login */
function PrivateRoute({ children }) {
  const auth = useAuth();
  if (auth.loading) return null;
  return auth.isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AppContent() {
  const auth = useAuth();
  const location = useLocation();                                // which page is open
  const isSuiteShellHidden = useHideSuiteShell(location.pathname); // Customer Butler, HR look

  if (auth.loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "var(--surface-page)", color: "var(--text-primary)" }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Client Portal — standalone, no navbar/sidebar */}
      <Route path="/report/:token" element={<ClientPortalPage />} />

      {/* Main App — with navbar */}
      <Route path="*" element={
        <ThemedApp>
          <div className="app">
            {isSuiteShellHidden ? null : <UnderConstructionBanner />}
            <OfflineIndicator />
            <ReSignInHost />
            {isSuiteShellHidden ? null : <Navbar />}
            <Routes>
              {/* The app's home IS the Butler Core hub (tile page) — James's
                  2026-06-04 sign-off. Marketing lives on the static site root;
                  the in-app LandingPage duplicate is unmounted. */}
              <Route path="/" element={<PrivateRoute><LauncherPage /></PrivateRoute>} />
              <Route path="/login" element={<LoginPage />} />
              {/* Legacy /app/inspection/* addresses (transition era — nginx still
                  302s butlercore.com/login to /app/inspection/login). The front
                  door now lives at /app; bounce the old paths to their new homes. */}
              <Route path="/inspection/login" element={<Navigate to="/login" replace />} />
              <Route path="/inspection" element={<Navigate to="/" replace />} />
              <Route path="/inspection/*" element={<Navigate to="/" replace />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* /apps is RETIRED as a destination (James 2026-06-04): the hub
                  lives at "/" (= butlercore.com/app). Tombstone redirect only. */}
              <Route path="/apps" element={<Navigate to="/" replace />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/inspections" element={<Navigate to="/" replace />} />
              <Route path="/inspections/new" element={<Navigate to="/" replace />} />
              <Route path="/inspections/:id" element={<Navigate to="/" replace />} />
              <Route path="/inspections/:id/report" element={<Navigate to="/" replace />} />
              <Route path="/inspections/:id/change-log" element={<Navigate to="/" replace />} />
              <Route path="/templates" element={<Navigate to="/" replace />} />
              <Route path="/templates/new" element={<Navigate to="/" replace />} />
              <Route path="/templates/:id" element={<Navigate to="/" replace />} />
              <Route path="/templates/:id/edit" element={<Navigate to="/" replace />} />
              <Route path="/schedule" element={<Navigate to="/" replace />} />
              <Route path="/customers" element={<PrivateRoute><CustomersEntry /></PrivateRoute>} />
              <Route path="/customers/:id" element={<PrivateRoute><CustomerEntry /></PrivateRoute>} />
              <Route path="/properties" element={<PrivateRoute><PropertiesEntry /></PrivateRoute>} />
              <Route path="/properties/:id" element={<PrivateRoute><PropertyEntry /></PrivateRoute>} />
              <Route path="/agents" element={<Navigate to="/" replace />} />
              <Route path="/pipeline" element={<Navigate to="/" replace />} />
              <Route path="/recurring" element={<Navigate to="/" replace />} />
              <Route path="/tasks" element={<Navigate to="/" replace />} />
              <Route path="/crm-setup" element={<PrivateRoute><CrmSetupEntry><CrmSettingsPage /></CrmSetupEntry></PrivateRoute>} />
              <Route path="/bridge" element={<PrivateRoute><BridgePage /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
              <Route path="/admin/blocked-patterns" element={<Navigate to="/" replace />} />

              {/* Well Butler (build step 7 — phone-first field app) */}
              <Route path="/well" element={<PrivateRoute><WellHomePage /></PrivateRoute>} />
              <Route path="/well/size" element={<PrivateRoute><SizePumpPage /></PrivateRoute>} />
              <Route path="/well/megtest" element={<PrivateRoute><MegTestPage /></PrivateRoute>} />
              <Route path="/well/diagnose" element={<PrivateRoute><DiagnosePage /></PrivateRoute>} />
              <Route path="/well/audit" element={<PrivateRoute><PhotoAuditPage /></PrivateRoute>} />
              <Route path="/well/trench" element={<PrivateRoute><TrenchEstimatePage /></PrivateRoute>} />
              <Route path="/well/history" element={<PrivateRoute><WellHistoryPage /></PrivateRoute>} />
              <Route path="/well/summarize" element={<PrivateRoute><SummarizeReportPage /></PrivateRoute>} />
              <Route path="/build-log" element={<BuildLog />} />
              <Route path="/logout" element={<LogoutPage />} />
              {/* Catch-all: an unknown path must never render a blank, dead-end
                  document. Give it a real 404 with a way back to the hub. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </ThemedApp>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <DesignThemeProvider>
        <ToastProvider>
          {/* basename lets the app be served under a sub-path (e.g. /app) when
              built with PUBLIC_URL; empty in the normal root build, so production
              is unaffected. */}
          <Router basename={process.env.PUBLIC_URL || undefined}>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
        </ToastProvider>
      </DesignThemeProvider>
    </ThemeProvider>
  );
}

export default App;
